// #region Imports
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedFeeSchedule
} from "work/_shared/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Guid from "_shared/guids/guid";
import { FeeScheduleLoadIssue } from "marketplace/teams/fee-schedules/_stores/fee-schedule-redux-slice";
import { enqueueSnackbar } from "notistack";
import { useGetFeeScheduleTemplatesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { 
  FeeScheduleTemplateOrderParameters 
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { 
  getFeeScheduleTemplateQueryByInterface, 
  getFeeScheduleTemplatesByQuery, 
  isFeeScheduleTemplateLoadIssue, 
  updateFeeScheduleTemplateInterfaceQuery 
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { useSession } from "users/__session/session-context";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import Pagination from "@mui/material/Pagination";
import Loader from "_shared/_views/loading/loader";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
// #endregion

// #region Styled Components
const ListContainer = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(50),
  maxWidth: theme.spacing(50),
  overflowY: "auto",
}));
const PaginationControls = styled(Pagination)(({ theme }) => ({
  '&.MuiPagination-root > ul': {
    justifyContent: 'space-evenly'
  },
  marginBottom: theme.spacing(4),
  width: '100%'
}));
// #endregion

// #region Props/Types
type FeeScheduleCategoryListProps = {
  disabledCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule;
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void;
};

export default function FeeScheduleCategoryList({
  disabledCategories,
  onCategoriesSelected,
}: Readonly<FeeScheduleCategoryListProps>) {
  const pageSize = 25;
  const debounceDelay = 300;

  // #region State
  const [rawSelectedNames, setRawSelectedNames] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const disabledNames = useMemo(() => {
    if (Masked.isMasked(disabledCategories)) {
      return new Set<string>();
    }
    return new Set(
      disabledCategories
        .map(getCategoryName)
        .filter((name): name is string => Boolean(name))
    );
  }, [disabledCategories]);

  const selectedNames = useMemo(() => {
    return new Set([...rawSelectedNames].filter((name) => !disabledNames.has(name)));
  }, [rawSelectedNames, disabledNames]);

  const canSubmit = useMemo(
    () => [...selectedNames].some((name) => !disabledNames.has(name)),
    [disabledNames, selectedNames]
  );
  // #region Hooks
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const interfaceName = 'feeScheduleTemplateCategories';
  const feeScheduleTemplatesQueryParams = useSelector(getFeeScheduleTemplateQueryByInterface(interfaceName));

  const paginatedFeeScheduleTemplatesJson = useSelector(getFeeScheduleTemplatesByQuery(feeScheduleTemplatesQueryParams));
  const feeScheduleTemplates = useMemo(() => {
    if (!paginatedFeeScheduleTemplatesJson) return;
    const deserializedData: ((FeeScheduleTemplate | FeeScheduleLoadIssue) & { id: Guid })[] = [];
    for (const feeScheduleTemplateJson of paginatedFeeScheduleTemplatesJson.data) {
      try {
        if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson)) {
          deserializedData.push(feeScheduleTemplateJson);
          continue;
        }

        const feeScheduleTemplate = FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
        if (feeScheduleTemplate?.id) {
          deserializedData.push(feeScheduleTemplate as FeeScheduleTemplate & { id: Guid });
          continue;
        }

        console.error("FeeScheduleTemplate does not have an id", feeScheduleTemplateJson);
        const issue = { id: Guid.EMPTY, issue: "Error loading FeeScheduleTemplate." };
        deserializedData.push(issue);
      } catch (error) {
        const id = "id" in feeScheduleTemplateJson 
          ? (Guid.fromJSON(feeScheduleTemplateJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading FeeScheduleTemplate." });
        console.error("Error deserializing FeeScheduleTemplate JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedFeeScheduleTemplatesJson]);

  const feeScheduleTemplatesAPIQuery = useGetFeeScheduleTemplatesQuery(
    feeScheduleTemplatesQueryParams ? { query: feeScheduleTemplatesQueryParams, interfaceName } : skipToken,
  );

  const categories = useMemo(() => feeScheduleTemplates
    ?.filter((template): template is FeeScheduleTemplate & { id: Guid } => !isFeeScheduleTemplateLoadIssue(template))
    .map(template => template.categories)
    .flat(2), [feeScheduleTemplates]);

  const availableCategories = useMemo(() => categories?.filter(
    (category) => isUnmaskedCategory(category)
  ), [categories]);

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceDelay);

    return () => globalThis.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (!session.user?.legalEntityId) {
      console.error("User does not have a legal entity ID");
      return;
    }

    const filterParameters = new FeeScheduleTemplateFilterParameters({
      entityId: session.user?.legalEntityId,
      query: debouncedSearchTerm,
    });
    const orderParameters = new FeeScheduleTemplateOrderParameters({
      orderBy: FeeScheduleTemplateOrderField.CreatedDate,
      orderDir: OrderDirection.Descending
    });

    const newQuery = new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters: new PaginationParameters({
        pageIndex: page - 1,
        pageSize,
      })
    });
    if (feeScheduleTemplatesQueryParams && newQuery.isEqualTo(feeScheduleTemplatesQueryParams)) return;
    dispatch(
      updateFeeScheduleTemplateInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [
    session.user?.legalEntityId.value,
    session,
    feeScheduleTemplatesQueryParams,
    dispatch,
    interfaceName,
    page,
    debouncedSearchTerm
  ]);

  useEffect(() => {
    if (feeScheduleTemplatesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedule templates table", { variant: "error" });
    }
  }, [feeScheduleTemplatesAPIQuery.isError]);

  // #region Functions
  const toggleCategorySelection = useCallback((categoryName: string) => {
    setRawSelectedNames((prev) => {
      const next = new Set(prev);

      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }

      return next;
    });
  }, []);

  const handleCategoryClick = useCallback(
    (category: FeeScheduleCategory) => {
      const categoryName = category.name.value;
      if (disabledNames.has(categoryName)) return;

      toggleCategorySelection(categoryName);
    },
    [disabledNames, toggleCategorySelection]
  );

  const handleAddSelectedClicked = useCallback(() => {
    const selectedCategories = availableCategories?.filter((category) =>
      selectedNames.has(category.name.value) && !disabledNames.has(category.name.value)
    ) ?? [];

    if (selectedCategories.length === 0) return;

    onCategoriesSelected(selectedCategories);
  }, [availableCategories, disabledNames, onCategoriesSelected, selectedNames]);
  // #endregion

  // #region Render
  if (feeScheduleTemplatesAPIQuery.isLoading) return <Loader />;

  return (
    <div>
      <TextField
        variant="outlined"
        fullWidth
        placeholder="Search Categories"
        disabled={!availableCategories?.length && !searchTerm.length}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        slotProps={{
          input: {
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            endAdornment: searchTerm.length > 0 && (
              <Tooltip title="Clear search">
                <IconButton onClick={() => setSearchTerm("")} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ),
          }
        }}
      />
      <ListContainer>
        {availableCategories?.length === 0 ? (
          <ListItem disableGutters>
            <ListItemText primary="No existing fee schedule categories" />
          </ListItem>
        ) : (
          availableCategories?.map((category: FeeScheduleCategory) => {
            const categoryName = category.name.value;
            const isDisabled = disabledNames.has(categoryName) || Masked.isMasked(disabledCategories);
            const isChecked = isDisabled || selectedNames.has(categoryName);

            return (
              <ListItemButton
                key={categoryName}
                alignItems="flex-start"
                disableGutters
                disabled={isDisabled}
                onClick={() => handleCategoryClick(category)}
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    checked={isChecked}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ whiteSpace: "pre-line" }}
                  primary={categoryName}
                  secondary={category.feeString}
                />
              </ListItemButton>
            );
          })
        )}
      </ListContainer>
      <PaginationControls 
        count={Math.ceil((availableCategories?.length ?? 0) / pageSize)}
        page={page}
        disabled={(availableCategories?.length ?? 0) < 25}
        onChange={(_event, value) => setPage(value)}
      />
      <Button
        color="primary"
        variant="contained"
        disabled={!canSubmit}
        startIcon={<AddIcon />}
        onClick={handleAddSelectedClicked}
      >
        Add Selected
      </Button>
    </div>
  );
  // #endregion
}

// #region Helper Functions
function isMaskedCategory(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
): category is FeeScheduleCategoryMaskedCurrency {
  return MaskedFeeSchedule.isMasked(category);
}

function isUnmaskedCategory(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
): category is FeeScheduleCategory {
  return !isMaskedCategory(category);
}

function getCategoryName(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.name?.value ?? "";
}
